import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last Updated: January 2025</p>
          
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 space-y-8 text-gray-300">
              
              <p className="leading-relaxed text-lg">
                Traain ("Traain," "we," "our," or "us") respects your privacy and is committed to protecting the personal information entrusted to us. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you visit our website, use our platform, or interact with our services.
              </p>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">1. Information We Collect</h2>
                <p className="leading-relaxed mb-4">We collect information in the following ways:</p>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-cyan-400 mb-2">Information You Provide Directly</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Name, email address, phone number</li>
                      <li>Company name, job title, and role</li>
                      <li>Account credentials and profile information</li>
                      <li>Communications with Traain (support requests, demos, inquiries)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-cyan-400 mb-2">Information Collected Through Platform Use</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Training participation and completion data</li>
                      <li>Role-play interactions and performance signals</li>
                      <li>Usage data related to features and functionality</li>
                      <li>Configuration and administrative preferences</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-cyan-400 mb-2">Automatically Collected Information</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>IP address, browser type, device information</li>
                      <li>Pages viewed and interactions with our website</li>
                      <li>Log files and analytics data</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">2. How We Use Information</h2>
                <p className="leading-relaxed mb-4">Traain uses information to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide, operate, and improve the Traain platform</li>
                  <li>Deliver training, role-play, and enablement services</li>
                  <li>Support onboarding, customer success, and technical support</li>
                  <li>Analyze platform usage to improve performance and reliability</li>
                  <li>Communicate updates, service notices, and relevant content</li>
                  <li>Maintain security, compliance, and system integrity</li>
                </ul>
                <p className="mt-4 font-semibold text-white">We do not sell personal data.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">3. AI, Training Data, and Performance Insights</h2>
                <p className="leading-relaxed mb-4">
                  Traain uses AI-powered systems, including Navigator AI™, to support training, role-play simulations, and performance insights.
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Training and role-play data is used to generate feedback, scoring, and readiness insights</li>
                  <li>AI models are used to improve learning experiences and platform performance</li>
                  <li>Customer data is not used to train public or third-party AI models</li>
                </ul>
                <p className="mt-4">All AI processing is governed by security, access controls, and contractual safeguards.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">4. Data Ownership and Customer Content</h2>
                <p className="leading-relaxed mb-4">Customers retain ownership of their content, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Training materials</li>
                  <li>Uploaded documentation</li>
                  <li>Role-play and performance data</li>
                </ul>
                <p className="mt-4">Traain processes customer data solely to provide contracted services and does not claim ownership of customer content.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">5. Sharing of Information</h2>
                <p className="leading-relaxed mb-4">We may share information only as necessary with:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Trusted service providers that support platform operations (hosting, analytics, security)</li>
                  <li>Legal or regulatory authorities when required by law</li>
                  <li>Business partners when explicitly authorized by the customer</li>
                </ul>
                <p className="mt-4">All third parties are required to maintain appropriate data protection standards.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">6. Data Security</h2>
                <p className="leading-relaxed">
                  Traain implements reasonable administrative, technical, and physical safeguards designed to protect information from unauthorized access, disclosure, or misuse.
                </p>
                <p className="mt-4">While no system is completely secure, we continuously evaluate and improve our security practices.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">7. Data Retention</h2>
                <p className="leading-relaxed mb-4">We retain information only as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Fulfill contractual obligations</li>
                  <li>Support platform functionality</li>
                  <li>Meet legal or regulatory requirements</li>
                </ul>
                <p className="mt-4">Customers may request deletion or export of data subject to applicable laws and agreements.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">8. Your Rights and Choices</h2>
                <p className="leading-relaxed mb-4">Depending on your jurisdiction, you may have rights to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Access or update your personal information</li>
                  <li>Request deletion of your data</li>
                  <li>Restrict or object to certain processing</li>
                  <li>Receive a copy of your data</li>
                </ul>
                <p className="mt-4">
                  Requests can be submitted to <a href="mailto:privacy@traain.com" className="text-cyan-400 hover:text-cyan-300">privacy@traain.com</a>.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">9. Cookies and Analytics</h2>
                <p className="leading-relaxed mb-4">Traain uses cookies and similar technologies to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Improve website functionality</li>
                  <li>Understand usage patterns</li>
                  <li>Enhance user experience</li>
                </ul>
                <p className="mt-4">You may manage cookie preferences through your browser settings.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">10. International Data Transfers</h2>
                <p className="leading-relaxed">
                  Traain may process data in the United States or other jurisdictions where service providers operate. We take steps to ensure appropriate safeguards are in place for international data transfers.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">11. Children's Privacy</h2>
                <p className="leading-relaxed">
                  Traain is not intended for use by individuals under the age of 16. We do not knowingly collect personal information from children.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">12. Changes to This Policy</h2>
                <p className="leading-relaxed">
                  We may update this Privacy Policy periodically. Updates will be posted on this page with a revised "Last Updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">13. Contact Us</h2>
                <p className="leading-relaxed">
                  If you have questions about this Privacy Policy or Traain's data practices, please contact:
                </p>
                <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                  <p className="font-semibold text-white">Traain</p>
                  <p>Email: <a href="mailto:support@traain.com" className="text-cyan-400 hover:text-cyan-300">support@traain.com</a></p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
